import {
  Text,
  Button,
  Input,
  useDesignTokens,
  Row,
  Column,
  Icon,
} from "@gradience/ui";
import { useRegister } from "../lib/auth";
import { useState } from "react";
import AuthChrome from "../components/auth-chrome";
import { isApiError } from "../lib/api";
import {
  createFileRoute,
  stripSearchParams,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useAppForm } from "../components/forms";
import { AddressFieldGroup } from "../components/forms/fields/address-field-group";
import { z } from "zod";
import { setFieldError } from "../components/forms/util";

export const Route = createFileRoute("/sign-up")({
  component: SignUp,
  validateSearch: z.object({
    step: z.number().optional().default(0),
  }),
});

type RegistrationFormValues = {
  roleAtSchool: string;
  schoolName: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  address: {
    line1: string;
    line2: string | undefined;
    city: string;
    state: string;
    zip: string;
  };
  schoolSize: string;
};

function SignUp() {
  const search = useSearch({ from: "/sign-up" });
  const navigate = useNavigate({ from: "/sign-up" });
  const designTokens = useDesignTokens();
  const registerMutation = useRegister();

  const form = useAppForm({
    defaultValues: {
      roleAtSchool: "",
      schoolName: "",
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      address: { line1: "", line2: "", city: "", state: "", zip: "" },
      schoolSize: "",
    } satisfies RegistrationFormValues,
    onSubmit: async ({ value }) => {
      await registerMutation.mutateAsync({
        body: {
          email: value.email,
          firstName: value.firstName,
          lastName: value.lastName,
          schoolName: value.schoolName,
          roleAtSchool: value.roleAtSchool,
          password: value.password,
          address: value.address,
        },
      });
    },
  });

  return (
    <AuthChrome>
      <form
        style={{ gap: 32, display: "flex", flexDirection: "column" }}
        onSubmit={(e) => {
          e.preventDefault();
          if (search.step === 0) {
            if (form.getFieldValue("roleAtSchool") === "") {
              form.setFieldMeta("roleAtSchool", (prev) => ({
                ...prev,
                errorMap: {
                  ...prev.errorMap,
                  onSubmit: [
                    prev.errorMap.onSubmit ?? null,
                    "Please select a role.",
                  ]
                    .filter((e) => e)
                    .join(", "),
                },
              }));
            } else {
              navigate({ search: (p) => ({ ...p, step: 1 }) });
            }
          } else {
            form.handleSubmit();
          }
        }}
      >
        <form.AppForm>
          <form.FormErrors />
        </form.AppForm>
        {registerMutation.isError && (
          <Text
            textStyle="strong"
            style={{
              color: designTokens.colors.text.brand,
            }}
          >
            {isApiError(registerMutation.error)
              ? registerMutation.error.response?.data.errors
                  .map((e) => e.message)
                  .join(", ")
              : "Something went wrong."}
          </Text>
        )}
        {search.step === 0 ? (
          <>
            <form.AppField
              name="roleAtSchool"
              children={(field) => (
                <field.InputField
                  label="Your Role"
                  helpText="The person managing the ULE should be a head of school, principal, or other school administrator. You will be able to add other users to the school account later."
                  required
                  type="select"
                  options={[
                    { label: "Select One", value: "" },
                    { label: "Headmaster", value: "Headmaster" },
                    { label: "Principal", value: "Principal" },
                    { label: "Head of School", value: "Head of School" },
                    { label: "Superintendent", value: "Superintendent" },
                    {
                      label: "Other School Director Role",
                      value: "Other School Director Role",
                    },
                  ]}
                />
              )}
            />
            <Row gap={8} style={{ alignItems: "flex-end" }}>
              <form.AppField
                name="firstName"
                children={(field) => (
                  <field.TextField
                    label="Your Name"
                    required
                    placeholder="First Name"
                  />
                )}
              />
              <form.AppField
                name="lastName"
                children={(field) => (
                  <field.TextField required placeholder="Last Name" />
                )}
              />
            </Row>

            <form.AppField
              name="email"
              children={(field) => (
                <field.TextField
                  type="email"
                  label="Email"
                  required
                  placeholder="Email Address"
                  helpText="Please use your school email."
                />
              )}
            />
            <form.AppField
              name="password"
              children={(field) => (
                <field.InputField
                  type="password"
                  label="Password"
                  required
                  placeholder="Create a password"
                />
              )}
            />

            <form.AppForm>
              <form.SubmitButton label="Next" />
            </form.AppForm>
          </>
        ) : (
          <>
            <Icon
              name="arrow-left"
              onClick={() => navigate({ search: (p) => ({ ...p, step: 0 }) })}
              style={{ alignSelf: "flex-start" }}
            />
            <form.AppField
              name="schoolName"
              children={(field) => (
                <field.TextField
                  label="School Name"
                  required
                  placeholder="Enter School Name"
                />
              )}
            />
            <AddressFieldGroup
              form={form}
              fields={{
                line1: "address.line1",
                line2: "address.line2",
                city: "address.city",
                state: "address.state",
                zip: "address.zip",
              }}
            />
            <form.AppField
              name="schoolSize"
              validators={{
                onSubmit: ({ value }) => {
                  if (value === "") {
                    return "Please select a school size.";
                  }
                },
              }}
              children={(field) => (
                <field.InputField
                  label="School Size"
                  required
                  type="select"
                  options={[
                    { label: "Select", value: "" },
                    { label: "1-50", value: "1-50" },
                    { label: "51-100", value: "51-100" },
                    { label: "101-200", value: "101-200" },
                    { label: "201-500", value: "201-500" },
                    {
                      label: "501+",
                      value: "501+",
                    },
                  ]}
                />
              )}
            />
            <form.AppForm>
              <form.SubmitButton label="Create Account" />
            </form.AppForm>
          </>
        )}
      </form>
    </AuthChrome>
  );
}

export default SignUp;
